import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  LogIn,
  LogOut,
  PanelLeft,
  UserRound,
} from 'lucide-react';
import { Badge } from './Badge';
import { Button } from './Button';
import { Card, CardContent } from './Card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './DropdownMenu';
import { Separator } from './Separator';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  useSidebar,
} from './Sidebar';
import { TooltipProvider } from './Tooltip';

export interface PortalNavItem {
  label: string;
  href: string;
  icon: ReactNode;
  active?: boolean;
  badge?: string;
}

export interface PortalNavGroup {
  id: string;
  label: string;
  icon: ReactNode;
  items: PortalNavItem[];
}

export type PortalNavEntry = PortalNavItem | PortalNavGroup;

export interface PortalNavLinkProps {
  href: string;
  className: string;
  active: boolean;
  onClick?: () => void;
  children: ReactNode;
}

export type PortalNavLinkRenderer = (props: PortalNavLinkProps) => ReactElement;

const renderAnchorNavLink: PortalNavLinkRenderer = ({
  href,
  className,
  active,
  onClick,
  children,
}) => (
  <a className={className} href={href} aria-current={active ? 'page' : undefined} onClick={onClick}>
    {children}
  </a>
);

export interface PortalMetric {
  label: string;
  value: string;
  tone?: 'default' | 'accent' | 'danger' | 'success';
}

export interface PortalShellProps {
  brand: string;
  sidebarBrandMark?: ReactNode;
  editionLabel: string;
  localeLabel: string;
  navLabel: string;
  showSidebarNavLabel?: boolean;
  userLabel: string;
  accountName: string;
  accountDetail?: string;
  navItems: PortalNavEntry[];
  topItems?: PortalNavItem[];
  metrics?: PortalMetric[];
  children: ReactNode;
  actions?: ReactNode;
  sidebarFooter?: ReactNode;
  topbarAccount?: ReactNode;
  userMenuItems?: PortalNavItem[];
  menuLabel?: string;
  closeMenuLabel?: string;
  renderNavLink?: PortalNavLinkRenderer;
  accessActionLabel?: string;
  onAccessAction?: () => void;
  authenticated?: boolean;
  collapseLabel?: string;
  expandLabel?: string;
}

const SIDEBAR_COLLAPSED_KEY = 'ignite.portal.sidebar.collapsed';

function isPortalNavGroup(entry: PortalNavEntry): entry is PortalNavGroup {
  return 'items' in entry;
}

function activeGroupIds(entries: PortalNavEntry[]): string[] {
  return entries
    .filter(isPortalNavGroup)
    .filter((group) => group.items.some((item) => item.active))
    .map((group) => group.id);
}

function metricBadgeVariant(tone: PortalMetric['tone']) {
  if (tone === 'danger') return 'destructive' as const;
  if (tone === 'accent') return 'outline' as const;
  if (tone === 'success') return 'secondary' as const;
  return 'ghost' as const;
}

export function PortalShell(props: PortalShellProps) {
  const [open, setOpen] = useState(true);

  useEffect(() => {
    try {
      setOpen(window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) !== 'true');
    } catch {
      setOpen(true);
    }
  }, []);

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen);
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(!nextOpen));
    } catch {
      // 浏览器禁用存储时仍允许本次会话切换。
    }
  }

  return (
    <TooltipProvider>
      <SidebarProvider open={open} onOpenChange={handleOpenChange}>
        <PortalShellContent {...props} />
      </SidebarProvider>
    </TooltipProvider>
  );
}

function PortalShellContent({
  brand,
  sidebarBrandMark,
  editionLabel,
  localeLabel,
  navLabel,
  showSidebarNavLabel = true,
  userLabel,
  accountName,
  accountDetail,
  navItems,
  topItems = [],
  metrics = [],
  children,
  actions,
  sidebarFooter,
  topbarAccount,
  userMenuItems = [],
  menuLabel = 'Menu',
  renderNavLink = renderAnchorNavLink,
  accessActionLabel,
  onAccessAction,
  authenticated = false,
  collapseLabel = 'Collapse sidebar',
  expandLabel = 'Expand sidebar',
}: PortalShellProps) {
  const { state, isMobile, toggleSidebar, setOpenMobile } = useSidebar();
  const showSidebarText = state === 'expanded' || isMobile;
  const resolvedSidebarBrandMark =
    sidebarBrandMark === undefined ? <Badge>{brand.slice(0, 1)}</Badge> : sidebarBrandMark;
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set(activeGroupIds(navItems)),
  );

  useEffect(() => {
    const activeIds = activeGroupIds(navItems);
    if (activeIds.length === 0) return;

    setExpandedGroups((current) => {
      if (activeIds.every((id) => current.has(id))) return current;
      return new Set([...current, ...activeIds]);
    });
  }, [navItems]);

  function closeMobileSidebar() {
    if (isMobile) setOpenMobile(false);
  }

  function createNavLink(item: PortalNavItem, children: ReactNode) {
    return renderNavLink({
      href: item.href,
      className: '',
      active: Boolean(item.active),
      onClick: closeMobileSidebar,
      children,
    });
  }

  function renderNavItem(item: PortalNavItem) {
    return (
      <SidebarMenuItem key={item.href}>
        <SidebarMenuButton
          render={createNavLink(
            item,
            <>
              {item.icon}
              <span>{item.label}</span>
            </>,
          )}
          isActive={Boolean(item.active)}
          tooltip={item.label}
        />
        {item.badge ? <SidebarMenuBadge>{item.badge}</SidebarMenuBadge> : null}
      </SidebarMenuItem>
    );
  }

  function renderCollapsedGroup(group: PortalNavGroup, active: boolean) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger
          render={<SidebarMenuButton isActive={active} aria-label={group.label} />}
        >
          {group.icon}
          <span>{group.label}</span>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="start">
          {group.items.map((item) => (
            <DropdownMenuItem
              key={item.href}
              render={createNavLink(
                item,
                <>
                  {item.icon}
                  <span>{item.label}</span>
                </>,
              )}
            />
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  function renderNavGroup(group: PortalNavGroup) {
    const expanded = expandedGroups.has(group.id);
    const active = group.items.some((item) => item.active);

    return (
      <SidebarMenuItem key={group.id}>
        {state === 'collapsed' && !isMobile ? (
          renderCollapsedGroup(group, active)
        ) : (
          <>
            <SidebarMenuButton
              type="button"
              isActive={active}
              onClick={() => {
                setExpandedGroups((current) => {
                  const next = new Set(current);
                  if (next.has(group.id)) next.delete(group.id);
                  else next.add(group.id);
                  return next;
                });
              }}
            >
              {group.icon}
              <span>{group.label}</span>
              {expanded ? <ChevronUp /> : <ChevronDown />}
            </SidebarMenuButton>
            {expanded ? (
              <SidebarMenuSub>
                {group.items.map((item) => (
                  <SidebarMenuSubItem key={item.href}>
                    <SidebarMenuSubButton
                      render={createNavLink(
                        item,
                        <>
                          {item.icon}
                          <span>{item.label}</span>
                        </>,
                      )}
                      isActive={Boolean(item.active)}
                    />
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            ) : null}
          </>
        )}
      </SidebarMenuItem>
    );
  }

  return (
    <>
      <Sidebar collapsible="icon">
        <SidebarHeader>
          <div className="flex min-w-0 items-center gap-2 px-2 py-1">
            {resolvedSidebarBrandMark}
            {showSidebarText ? (
              <div className="grid min-w-0 flex-1">
                <strong>{brand}</strong>
                <small>{editionLabel}</small>
              </div>
            ) : null}
          </div>
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            {showSidebarNavLabel ? <SidebarGroupLabel>{navLabel}</SidebarGroupLabel> : null}
            <SidebarGroupContent>
              <SidebarMenu className="gap-1">
                {navItems.map((entry) =>
                  isPortalNavGroup(entry) ? renderNavGroup(entry) : renderNavItem(entry),
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          {sidebarFooter}
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <SidebarMenuButton
                      size="lg"
                      tooltip={userLabel}
                      className="group-data-[collapsible=icon]:justify-center"
                    />
                  }
                >
                  <UserRound />
                  {showSidebarText ? (
                    <span className="grid min-w-0 flex-1">
                      <strong>{accountName}</strong>
                      <small>{accountDetail || localeLabel}</small>
                    </span>
                  ) : null}
                  {showSidebarText ? <ChevronRight /> : null}
                </DropdownMenuTrigger>
                <DropdownMenuContent side="right" align="end">
                  {userMenuItems.map((item) => (
                    <DropdownMenuItem
                      key={item.href}
                      render={createNavLink(
                        item,
                        <>
                          {item.icon}
                          <span>{item.label}</span>
                        </>,
                      )}
                    />
                  ))}
                  {accessActionLabel && onAccessAction ? <DropdownMenuSeparator /> : null}
                  {accessActionLabel && onAccessAction ? (
                    <DropdownMenuItem
                      variant={authenticated ? 'destructive' : 'default'}
                      onClick={onAccessAction}
                    >
                      {authenticated ? <LogOut /> : <LogIn />}
                      <span>{accessActionLabel}</span>
                    </DropdownMenuItem>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>
      <SidebarInset>
        <header className="flex min-h-14 items-center gap-2 px-4">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={isMobile ? menuLabel : state === 'collapsed' ? expandLabel : collapseLabel}
            title={isMobile ? menuLabel : state === 'collapsed' ? expandLabel : collapseLabel}
            onClick={toggleSidebar}
          >
            <PanelLeft />
          </Button>
          {topbarAccount}
          {topItems.length > 0 ? (
            <nav className="flex min-w-0 flex-1 items-center gap-1" aria-label={navLabel}>
              {topItems.map((item) => (
                <Button
                  key={item.href}
                  variant={item.active ? 'secondary' : 'ghost'}
                  size="default"
                  nativeButton={false}
                  render={createNavLink(item, item.label)}
                />
              ))}
            </nav>
          ) : (
            <div className="flex-1" />
          )}
          <div className="flex items-center gap-2">{actions}</div>
        </header>
        <Separator />
        {metrics.length > 0 ? (
          <section className="grid gap-3 p-4 md:grid-cols-3" aria-label={navLabel}>
            {metrics.map((metric) => (
              <Card key={metric.label} size="sm">
                <CardContent className="grid gap-1">
                  <span>{metric.label}</span>
                  <strong>{metric.value}</strong>
                  {metric.tone ? (
                    <Badge variant={metricBadgeVariant(metric.tone)}>{metric.tone}</Badge>
                  ) : null}
                </CardContent>
              </Card>
            ))}
          </section>
        ) : null}
        <main className="flex flex-1 flex-col bg-background p-4 md:p-6">{children}</main>
      </SidebarInset>
    </>
  );
}
